import { toast } from '../../../../util/toasts';
import { User } from '../../../../clients/quipli-service/user/user';
import { Location } from '../../../../clients/quipli-service/location/location';
import { ShippingType } from '../../../../clients/quipli-service/delivery/delivery';
import { OrderPaymentMethod } from '../../../../clients/quipli-service/order/order';
import { UserAddress } from '../../../../clients/quipli-service/user/user';
import { FinancialSummary } from '../../../../clients/quipli-service/financials/financials';
import { OnAccount } from '../../../../clients/quipli-service/payment/on-account/on-account';
import Dinero, { Currency } from 'dinero.js';
import { Constants } from '../../../../constants/constants';
import {
  CashPaymentMethod,
  CheckPaymentMethod,
  CustomPaymentMethod,
  OnAccountPaymentMethodRequest,
  PaymentMethodRequest,
  PaymentMethodType,
  StripePaymentMethodRequest,
} from '../../../../clients/quipli-service/payment/payment';

export const canSubmitOrder = (
  customer: User | null,
  location: Location | null,
  summary: FinancialSummary | undefined,
  selectedDelivery: ShippingType | null,
  isCreditMemo: boolean,
  deliveryAddress: UserAddress | undefined,
  paymentType: OrderPaymentMethod | undefined,
  onAccount: OnAccount | undefined,
) => {
  if (!customer) {
    toast('Please select a customer.', 'error');
    return false;
  }

  if (!location) {
    toast('Please select a location.', 'error');
    return false;
  }

  if (!summary) {
    toast('Please add products.', 'error');
    return false;
  }

  if (!selectedDelivery) {
    toast('Please select a delivery option.', 'error');
    return false;
  }

  if (isCreditMemo && selectedDelivery !== ShippingType.SelfPickUp && !deliveryAddress) {
    toast('Please select a delivery address.', 'error');
    return false;
  }

  if (paymentType === OrderPaymentMethod.OnAccount && !onAccount) {
    toast('Please set up an on account payment method.', 'error');
    return false;
  }

  return true;
};

export const evaluateSubmission = (
  customer: User | null,
  location: Location | null,
  summary: FinancialSummary | undefined,
  selectedDelivery: ShippingType | null,
  isCreditMemo: boolean,
  deliveryAddress: UserAddress | undefined,
  paymentType: OrderPaymentMethod | undefined,
  onAccount: OnAccount | undefined,
  balance: Dinero.Dinero | undefined,
  hasPermissions: (permissions: string | string[]) => boolean,
  setTotalExceedsLimit: (value: boolean) => void,
  submit: () => Promise<false | undefined>,
) => {
  if (
    !canSubmitOrder(
      customer,
      location,
      summary,
      selectedDelivery,
      isCreditMemo,
      deliveryAddress,
      paymentType,
      onAccount,
    )
  ) {
    return;
  }
  const cartTotal = summary!.lineItems.reduce(
    (acc, line) => (line.type === 'Product' ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location!.currency as Currency }),
  );
  if (
    customer?.creditLimit &&
    !customer.creditLimit.isZero() &&
    !isCreditMemo &&
    cartTotal
      .add(balance ?? Dinero({ amount: 0, currency: location?.currency as Currency }))
      .greaterThan(customer.creditLimit)
  ) {
    setTotalExceedsLimit(true);
    !hasPermissions(Constants.Permissions.Credit_Limit_Adjustment) &&
      toast('Order total exceeds credit limit.', 'error');
    return;
  } else {
    submit();
  }
};

export const buildPaymentMethod = (
  isQuote: boolean,
  isMissedRental: boolean,
  isCreditMemo: boolean,
  paymentType: OrderPaymentMethod | undefined,
  bankAccountId: string | undefined,
  cardId: number | undefined,
  poNumber: string | undefined,
  customPaymentMethod: string | undefined | null,
): PaymentMethodRequest => {
  if (isQuote || isMissedRental) {
    return { type: PaymentMethodType.None };
  } else if (isCreditMemo) {
    return { type: PaymentMethodType.Credit };
  }
  let paymentMethod: PaymentMethodRequest;
  switch (paymentType) {
    case OrderPaymentMethod.BankAccount:
      paymentMethod = { type: PaymentMethodType.Stripe, bankAccountId, poNumber } as StripePaymentMethodRequest;
      break;
    case OrderPaymentMethod.CreditCard:
      paymentMethod = { type: PaymentMethodType.Stripe, cardId, poNumber } as StripePaymentMethodRequest;
      break;
    case OrderPaymentMethod.Terminal:
      paymentMethod = { type: PaymentMethodType.Stripe, cardId: -1, poNumber } as StripePaymentMethodRequest;
      break;
    case OrderPaymentMethod.OnAccount:
      paymentMethod = {
        type: PaymentMethodType.OnAccount,
        accountNumber: '',
        poNumber,
      } as OnAccountPaymentMethodRequest;
      break;
    case OrderPaymentMethod.Cash:
      paymentMethod = { type: PaymentMethodType.Cash, poNumber } as CashPaymentMethod;
      break;
    case OrderPaymentMethod.Check:
      paymentMethod = { type: PaymentMethodType.Check, poNumber } as CheckPaymentMethod;
      break;
    case OrderPaymentMethod.Custom:
      paymentMethod = {
        type: PaymentMethodType.Custom,
        name: customPaymentMethod,
        poNumber,
      } as CustomPaymentMethod;
      break;
    case OrderPaymentMethod.ProceedWithoutPayment:
    default:
      paymentMethod = { type: PaymentMethodType.None, poNumber };
      break;
  }
  return paymentMethod;
};
